import { useEffect, useRef, useState } from "react";
import { UserPlus, Link2, RefreshCw, Trash2, MailCheck, MailX, Camera, ImageMinus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  aktiv: { label: "Aktiv", cls: "text-emerald-600 bg-emerald-500/10" },
  invitert: { label: "Invitert", cls: "text-amber-600 bg-amber-500/10" },
};

function BrukerRad({ b, megId, onReinvite, onSlett, onAvatar, onFjernAvatar, jobber }) {
  const s = STATUS[b.status] || STATUS.invitert;
  const filRef = useRef(null);
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-t border-neutral-200 first:border-t-0"
      data-testid={`bruker-rad-${b.id}`}>
      <button type="button" onClick={() => filRef.current?.click()} title="Last opp profilbilde"
        className="relative w-9 h-9 rounded-full bg-[#8B5CF6] text-white text-sm font-bold flex items-center justify-center shrink-0 overflow-hidden group"
        data-testid={`bruker-avatar-${b.id}`}>
        {b.avatarUrl
          ? <img src={b.avatarUrl} alt={b.name} className="absolute inset-0 w-full h-full object-cover" />
          : (b.name || b.email)[0].toUpperCase()}
        <span className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <Camera size={13} />
        </span>
      </button>
      <input ref={filRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onAvatar(b, f); e.target.value = ""; }}
        data-testid={`bruker-avatar-input-${b.id}`} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[15px] truncate" data-testid={`bruker-navn-${b.id}`}>
          {b.name}{b.id === megId && <span className="ml-2 text-xs font-medium text-neutral-400">deg</span>}
        </p>
        <p className="text-[13px] text-neutral-500 truncate">{b.email}{b.invitedBy ? ` · invitert av ${b.invitedBy}` : ""}</p>
      </div>
      <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${s.cls}`}
        data-testid={`bruker-status-${b.id}`}>{s.label}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        {b.avatarUrl && (
          <button type="button" onClick={() => onFjernAvatar(b)} disabled={jobber}
            title="Fjern profilbilde"
            className="p-2 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            data-testid={`bruker-avatar-fjern-${b.id}`}>
            <ImageMinus size={15} />
          </button>
        )}
        <button type="button" onClick={() => onReinvite(b)} disabled={jobber}
          title="Send invitasjonslenke på nytt"
          className="p-2 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          data-testid={`bruker-reinvite-${b.id}`}>
          <RefreshCw size={15} />
        </button>
        {b.id !== megId && (
          <button type="button" onClick={() => onSlett(b)} disabled={jobber}
            title="Slett bruker"
            className="p-2 rounded-lg text-neutral-500 hover:text-red-600 hover:bg-red-500/10 transition-colors"
            data-testid={`bruker-slett-${b.id}`}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function BrukereAdmin() {
  const [brukere, setBrukere] = useState(null);
  const [epostOk, setEpostOk] = useState(true);
  const [megId, setMegId] = useState(null);
  const [navn, setNavn] = useState("");
  const [epost, setEpost] = useState("");
  const [jobber, setJobber] = useState(false);
  const [sisteLenke, setSisteLenke] = useState(null);
  const [slettBekreft, setSlettBekreft] = useState(null);

  const hent = async () => {
    try {
      const [{ data }, me] = await Promise.all([api.get("/admin/brukere"), api.get("/admin/me")]);
      setBrukere(data.items);
      setEpostOk(data.epostKonfigurert);
      setMegId(me.data.id);
    } catch { setBrukere([]); }
  };
  useEffect(() => { hent(); }, []);

  const inviter = async (e) => {
    e.preventDefault();
    setJobber(true);
    try {
      const { data } = await api.post("/admin/brukere", { name: navn.trim(), email: epost.trim() });
      setSisteLenke({ email: data.item.email, url: data.inviteUrl, epost: data.epost });
      toast.success(data.epost === "accepted" ? `Invitasjon sendt til ${data.item.email}` : "Bruker opprettet — kopier lenken under");
      setNavn(""); setEpost("");
      hent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setJobber(false);
  };

  const reinvite = async (b) => {
    setJobber(true);
    try {
      const { data } = await api.post(`/admin/brukere/${b.id}/reinvite`);
      setSisteLenke({ email: b.email, url: data.inviteUrl, epost: data.epost });
      toast.success(data.epost === "accepted" ? `Ny invitasjon sendt til ${b.email}` : "Ny lenke laget — kopier under");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setJobber(false);
  };

  const slett = async (b) => {
    if (slettBekreft !== b.id) { setSlettBekreft(b.id); toast.info(`Trykk slett igjen for å fjerne ${b.name}`); return; }
    setJobber(true);
    try {
      await api.delete(`/admin/brukere/${b.id}`);
      toast.success(`${b.name} er slettet`);
      setSlettBekreft(null);
      hent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setJobber(false);
  };

  const lastOppAvatar = async (b, fil) => {
    setJobber(true);
    try {
      const fd = new FormData();
      fd.append("fil", fil);
      await api.post(`/admin/brukere/${b.id}/avatar`, fd);
      toast.success(`Profilbilde oppdatert for ${b.name}`);
      hent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setJobber(false);
  };

  const fjernAvatar = async (b) => {
    setJobber(true);
    try {
      await api.delete(`/admin/brukere/${b.id}/avatar`);
      toast.success("Profilbilde fjernet");
      hent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setJobber(false);
  };

  const kopier = () => {
    navigator.clipboard.writeText(sisteLenke.url).then(() => toast.success("Lenke kopiert"));
  };

  return (
    <div className="max-w-3xl" data-testid="brukere-side">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Switzer, sans-serif" }}>Users</h1>
      <p className="text-sm text-neutral-500 mt-1">Inviter teamet inn i Event OS — hver bruker velger sitt eget passord og blir automatisk tilgjengelig som eier av saker, targets og outreach.</p>

      <form onSubmit={inviter} className="mt-8 bg-white border border-neutral-200 rounded-2xl p-5" data-testid="inviter-panel">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-400 mb-3">Inviter ny bruker</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={navn} onChange={(e) => setNavn(e.target.value)} placeholder="Navn" required
            className="flex-1 bg-transparent border border-neutral-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-neutral-400"
            data-testid="inviter-navn" />
          <input value={epost} onChange={(e) => setEpost(e.target.value)} placeholder="navn@firma.no" type="email" required
            className="flex-1 bg-transparent border border-neutral-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-neutral-400"
            data-testid="inviter-epost" />
          <button type="submit" disabled={jobber || !navn.trim() || !epost.trim()}
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-semibold px-5 py-2.5 rounded-xl disabled:opacity-40 transition-opacity"
            data-testid="inviter-send">
            <UserPlus size={15} /> {jobber ? "Sender …" : "Send invitasjon"}
          </button>
        </div>
        {!epostOk && (
          <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5" data-testid="inviter-epost-advarsel">
            <MailX size={13} /> E-postutsending er ikke konfigurert — du får en lenke du kan dele manuelt.
          </p>
        )}
        {sisteLenke && (
          <div className="mt-4 flex items-center gap-2 border border-neutral-200 rounded-xl px-3 py-2" data-testid="inviter-lenke">
            <MailCheck size={14} className="text-emerald-600 shrink-0" />
            <span className="text-xs text-neutral-500 truncate flex-1">{sisteLenke.url}</span>
            <button type="button" onClick={kopier}
              className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 hover:text-neutral-900 px-2 py-1 rounded-lg hover:bg-neutral-100 transition-colors"
              data-testid="inviter-kopier">
              <Link2 size={12} /> Kopier
            </button>
          </div>
        )}
      </form>

      <div className="mt-6 bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="bruker-liste">
        {brukere === null && <p className="px-5 py-6 text-sm text-neutral-400">Laster …</p>}
        {brukere !== null && brukere.length === 0 && <p className="px-5 py-6 text-sm text-neutral-400">Ingen brukere ennå.</p>}
        {(brukere || []).map((b) => (
          <BrukerRad key={b.id} b={b} megId={megId} onReinvite={reinvite} onSlett={slett}
            onAvatar={lastOppAvatar} onFjernAvatar={fjernAvatar} jobber={jobber} />
        ))}
      </div>
    </div>
  );
}
